import { useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet, FileText, Activity } from "lucide-react";
import { keyMode } from "@/lib/atlas-api";
import { downloadCsv } from "@/lib/atlas-csv";
import { Card } from "@/components/portal/PortalPrimitives";

function ReportCard({ icon: Icon, title, description, action, accent = false, testId, children }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-[12px] border p-5 sm:p-6 ${
        accent ? "bg-[#1A4232] border-[#1A4232] text-[#F5F1EB]" : "bg-white border-slate-200/80"
      }`}
    >
      <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">
        <span className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] ${accent ? "bg-[#F5F1EB] text-[#1A4232]" : "bg-[#1A4232] text-[#F5F1EB]"}`}>
          <Icon className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className={`font-display text-[18px] ${accent ? "text-[#F5F1EB]" : "text-[#0A1128]"}`}>{title}</h3>
          <p className={`mt-1 text-[13px] ${accent ? "text-[#F5F1EB]/85" : "text-slate-600"}`}>{description}</p>
          {children && <div className="mt-4">{children}</div>}
        </div>
        <div className="shrink-0">{action}</div>
      </div>
    </div>
  );
}

function dateNDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

export default function PortalReports() {
  const [busy, setBusy] = useState(null);
  const [calcSince, setCalcSince] = useState(dateNDaysAgo(30));
  const [calcUntil, setCalcUntil] = useState("");
  const [whSince, setWhSince] = useState(dateNDaysAgo(30));
  const [whUntil, setWhUntil] = useState("");
  const [whStatus, setWhStatus] = useState("");
  const [usageMonths, setUsageMonths] = useState(6);
  const env = keyMode();
  const envTag = env === "production" ? "production" : "sandbox";

  const run = async (label, fn) => {
    setBusy(label);
    try {
      await fn();
      toast.success(`${label} downloaded`);
    } catch (e) {
      toast.error(e.message || `${label} failed`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">Reports</p>
        <h1 className="font-display text-[28px] text-[#0A1128] mt-1.5">Download CSV exports</h1>
        <p className="mt-2 text-[13.5px] text-slate-600 max-w-2xl">
          Pull your data out of AtlasForge for BI, audit, or your data lake. Reports are
          scoped to your <strong>{envTag}</strong> environment — sign in with your other key
          to export the other side.
        </p>
      </div>

      <ReportCard
        testId="report-calculations"
        icon={FileSpreadsheet}
        title="Safe-to-Spend calculations"
        description="Every cached calculation: end-user, breakdown, daily allowance, calculation id. One row per end-user, newest first."
        action={
          <button
            disabled={busy === "Calculations"}
            data-testid="report-calc-download"
            onClick={() =>
              run("Calculations", () =>
                downloadCsv({
                  path: "/api/v1/reports/calculations.csv",
                  filename: `atlas-${envTag}-calculations-${new Date().toISOString().slice(0,10)}.csv`,
                  needsIngest: true,
                  params: { since: calcSince || undefined, until: calcUntil || undefined },
                })
              )
            }
            className="inline-flex items-center gap-2 rounded-full bg-[#1A4232] text-[#F5F1EB] text-[13px] font-medium px-5 py-2.5 hover:bg-[#0E2A20] transition-colors disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            {busy === "Calculations" ? "Preparing…" : "Download CSV"}
          </button>
        }
      >
        <div className="grid sm:grid-cols-2 gap-3 max-w-md">
          <DateField label="Calculated since" value={calcSince} onChange={setCalcSince} testId="report-calc-since" />
          <DateField label="Calculated until" value={calcUntil} onChange={setCalcUntil} testId="report-calc-until" />
        </div>
      </ReportCard>

      <ReportCard
        testId="report-usage"
        icon={Activity}
        title="Monthly usage & billing"
        description="One row per month: production + sandbox calls, unique users, and estimated production bill."
        accent
        action={
          <button
            disabled={busy === "Usage"}
            data-testid="report-usage-download"
            onClick={() =>
              run("Usage", () =>
                downloadCsv({
                  path: "/api/v1/reports/usage.csv",
                  filename: `atlas-usage-last-${usageMonths}-months.csv`,
                  needsIngest: false,
                  params: { months: usageMonths },
                })
              )
            }
            className="inline-flex items-center gap-2 rounded-full bg-[#F5F1EB] text-[#1A4232] text-[13px] font-medium px-5 py-2.5 hover:bg-white transition-colors disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            {busy === "Usage" ? "Preparing…" : "Download CSV"}
          </button>
        }
      >
        <label className="text-[12px] uppercase tracking-[0.14em] text-[#F5F1EB]/70">Months</label>
        <select
          value={usageMonths}
          onChange={(e) => setUsageMonths(Number(e.target.value))}
          data-testid="report-usage-months"
          className="mt-1 ml-0 sm:ml-2 rounded-[10px] bg-[#F5F1EB] text-[#1A4232] text-[13px] px-3 py-1.5 border border-[#F5F1EB]/40"
        >
          {[3, 6, 12, 24].map((n) => (
            <option key={n} value={n}>last {n} months</option>
          ))}
        </select>
      </ReportCard>

      <ReportCard
        testId="report-webhooks"
        icon={FileText}
        title="Webhook deliveries"
        description="Every delivery attempt: event type, status, response code, attempts, latency, timestamp."
        action={
          <button
            disabled={busy === "Webhooks"}
            data-testid="report-webhook-download"
            onClick={() =>
              run("Webhooks", () =>
                downloadCsv({
                  path: "/api/v1/reports/webhooks.csv",
                  filename: `atlas-webhook-deliveries-${new Date().toISOString().slice(0,10)}.csv`,
                  needsIngest: true,
                  params: { since: whSince || undefined, until: whUntil || undefined, status: whStatus || undefined },
                })
              )
            }
            className="inline-flex items-center gap-2 rounded-full bg-[#1A4232] text-[#F5F1EB] text-[13px] font-medium px-5 py-2.5 hover:bg-[#0E2A20] transition-colors disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            {busy === "Webhooks" ? "Preparing…" : "Download CSV"}
          </button>
        }
      >
        <div className="grid sm:grid-cols-3 gap-3 max-w-2xl">
          <DateField label="Since" value={whSince} onChange={setWhSince} testId="report-wh-since" />
          <DateField label="Until" value={whUntil} onChange={setWhUntil} testId="report-wh-until" />
          <div>
            <label className="text-[12px] uppercase tracking-[0.14em] text-slate-500">Status</label>
            <select
              value={whStatus}
              onChange={(e) => setWhStatus(e.target.value)}
              data-testid="report-wh-status"
              className="mt-1 w-full rounded-[10px] border border-slate-300 px-3 py-1.5 text-[13px] bg-white"
            >
              <option value="">Any</option>
              <option value="delivered">Delivered</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>
      </ReportCard>

      <p className="text-[12px] text-slate-500">
        Reports are generated on demand from your live data. Large exports (&gt;10k rows) may take a few seconds to prepare.
      </p>
    </div>
  );
}

function DateField({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-[12px] uppercase tracking-[0.14em] text-slate-500">{label}</label>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-1 w-full rounded-[10px] border border-slate-300 px-3 py-1.5 text-[13px] bg-white"
      />
    </div>
  );
}
